import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Select, MenuItem, Box, Typography, CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import axios from "axios";
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import './App.css';
import { formatWatchlist } from "./watchlistFormat";
import { Cache } from "./cache";

const WEB_API_BASE_URL = "https://fastapp.doruk.org.tr";
// Build-time configurable so the dev pipeline (3031/3041) can point its
// frontend at the dev API instead of production's 3040.
const LOCAL_API_PORT = process.env.REACT_APP_LOCAL_API_PORT || "3040";

// True when the app was reached over plain http on a private/loopback
// address (RFC1918, loopback IPv4/IPv6) — i.e. a trusted local deployment.
const isLocalHttpAccess = () => {
  if (window.location.protocol !== "http:") return false;
  const host = window.location.hostname;
  if (host === "localhost" || host.startsWith("127.")) return true;
  if (host.startsWith("[")) return host === "[::1]" || host.startsWith("[fe80");
  const m = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  return a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31);
};

// Reached through a local IP over http -> talk to the API on that same IP.
// Any other access (web address, or https on any host) -> web API address.
const API_BASE_URL = isLocalHttpAccess()
  ? `http://${window.location.hostname}:${LOCAL_API_PORT}`
  : WEB_API_BASE_URL;

// Enforce HTTPS everywhere except plain-http calls to the local API itself
axios.interceptors.request.use((config) => {
  const url = new URL(config.url, config.baseURL || window.location.origin);
  const localApiHost = `${window.location.hostname}:${LOCAL_API_PORT}`;
  const isLocalApiCall = isLocalHttpAccess() && url.protocol === "http:" && url.host === localApiHost;
  if (!isLocalApiCall && url.protocol !== "https:") {
    throw new Error("Only HTTPS requests are allowed.");
  }
  return config;
});

// Shared theme factory — light is the default, dark kept as an option.
// Light palette: white surfaces, dark text, deeper green so text/CTA pass
// AA (>= 4.5:1) on white. Dark palette: the original fintech-dark tokens.
const makeTheme = (palette, inputBg, inputBorder, inputBorderHover) =>
  createTheme({
    palette,
    shape: { borderRadius: 8 },
    typography: {
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      subtitle2: { letterSpacing: "0.06em", textTransform: "uppercase" },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBg,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: inputBorder },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: inputBorderHover },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: { root: { fontSize: "0.9rem" } },
      },
    },
  });

const lightTheme = makeTheme(
  {
    mode: "light",
    primary: { main: "#0a6b4a", contrastText: "#ffffff" },
    error: { main: "#b71c1c" },
    background: { default: "#f4f6f8", paper: "#ffffff" },
    text: { primary: "#16202b", secondary: "#566270" },
    divider: "#dbe1e8",
  },
  "#ffffff",
  "#dbe1e8",
  "#b9c2cd"
);

const darkTheme = makeTheme(
  {
    mode: "dark",
    primary: { main: "#16c784", dark: "#12a06c", contrastText: "#06281b" },
    error: { main: "#f4555f" },
    background: { default: "#0b0e14", paper: "#11151d" },
    text: { primary: "#e6e9ef", secondary: "#8b93a3" },
    divider: "#1f2630",
  },
  "#11151d",
  "#1f2630",
  "#2a3342"
);

const CopyButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "copied",
})(({ theme, copied }) => ({
  minHeight: 48,
  fontWeight: 700,
  fontSize: "1rem",
  textTransform: "none",
  transition: theme.transitions.create(["background-color", "color"], {
    duration: 150,
  }),
  ...(copied && {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(22, 199, 132, 0.15)" : "rgba(11, 122, 83, 0.12)",
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark" ? "rgba(22, 199, 132, 0.22)" : "rgba(11, 122, 83, 0.18)",
    },
  }),
}));

const TwPanel = () => {
  const [exchanges, setExchanges] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [editorValue, setEditorValue] = useState("");
  const [copy_success, set_copy_success] = useState(false);
  const [isSpecialOption, setIsSpecialOption] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Light theme is the default; the choice persists across visits.
  const [isDark, setIsDark] = useState(() => {
    try {
      return window.localStorage.getItem("bister-theme") === "dark";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = isDark ? "dark" : "light";
    try {
      window.localStorage.setItem("bister-theme", isDark ? "dark" : "light");
    } catch {
      /* storage unavailable (private mode) — theme still works per-visit */
    }
  }, [isDark]);

  const theme = isDark ? darkTheme : lightTheme;

  const cacheRef = useRef(new Cache()); // Persistent cache

  const fetchExchanges = async () => {
    const cache = cacheRef.current;
    const cachedData = cache.get("exchanges");
    const cachedMarkets = cache.get("markets");

    if (cachedData && cachedMarkets) {
      setExchanges(cachedData);
      setMarkets(cachedMarkets);
      setSelectedMarket(cachedMarkets[0] || null);
      setSelectedExchange(cachedData[0] || null);
      return;
    }

    try {
      const [exchangesRes, marketsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/exchanges/`),
        axios.get(`${API_BASE_URL}/markets/`),
      ]);
      cache.set("exchanges", exchangesRes.data);
      cache.set("markets", marketsRes.data);
      setExchanges(exchangesRes.data);
      setMarkets(marketsRes.data);
      setSelectedMarket(marketsRes.data[0] || null);
      setSelectedExchange(exchangesRes.data[0] || null);
      setLoadError(false);
    } catch (error) {
      console.error("Error fetching exchanges:", error);
      setLoadError(true);
    }
  };

  // TradingView prefix config for the selected market, fetched from
  // /markets/{code}/prefixes: {default, symbols}
  const [tvPrefixes, setTvPrefixes] = useState(null);

  const fetchStocks = useCallback(async (exchangeCode) => {
    const cache = cacheRef.current;
    const cachedData = cache.get(exchangeCode);

    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/stocks/${exchangeCode}`);
      cache.set(exchangeCode, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching stocks for ${exchangeCode}:`, error);
      throw error;
    }
  }, []);

  // Fetch TradingView prefix config for a market (cached per market).
  const fetchTvPrefixes = useCallback(async (marketCode) => {
    const cache = cacheRef.current;
    const cached = cache.get(`prefixes:${marketCode}`);
    if (cached) return cached;
    try {
      const response = await axios.get(`${API_BASE_URL}/markets/${marketCode}/prefixes`);
      cache.set(`prefixes:${marketCode}`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching prefixes for ${marketCode}:`, error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (selectedMarket) {
      setTvPrefixes(null);
      fetchTvPrefixes(selectedMarket.code).then(setTvPrefixes);
    }
  }, [selectedMarket, fetchTvPrefixes]);

  const formatStocks = useCallback((exchangeCode, stocks, prefixes) => {
    return formatWatchlist(exchangeCode, stocks, prefixes);
  }, []);

  const fetchAndSetStocks = useCallback(async (exchange, setEditorValueFn, prefixes) => {
    try {
      const stocks = await fetchStocks(exchange.code);
      setEditorValueFn(formatStocks(exchange.code, stocks, prefixes));
    } catch (error) {
      console.error("Error fetching and setting stocks:", error);
    }
  }, [fetchStocks, formatStocks]);

  useEffect(() => {
    fetchExchanges();
  }, []);

  useEffect(() => {
    if (selectedExchange) {
      fetchAndSetStocks(selectedExchange, setEditorValue, tvPrefixes);
    }
  }, [selectedExchange, fetchAndSetStocks, tvPrefixes]);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(editorValue)
      .then(() => {
        set_copy_success(true);
        setTimeout(() => {
          set_copy_success(false);
        }, 3000); // Reset after 3 seconds
      })
      .catch((err) => {
        console.error("Clipboard API failed:", err);
        fallbackCopy(editorValue);
      });
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed"; // Prevent scrolling to the bottom of the page
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
      console.log("Copied using fallback method:", text);
    } catch (err) {
      console.error("Fallback copy failed:", err);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const generateSpecialFormat = () => {
    const formatFilteredStocks = (header, mainStocks, excludeStocks) => {
      const mainCodes = mainStocks.map((stock) => stock.code);
      const excludeCodes = excludeStocks.map((stock) => stock.code);
      const filteredCodes = mainCodes.filter((code) => !excludeCodes.includes(code));
      return `###${header}, ${filteredCodes.map((code) => `BIST:${code}`).join(", ")}`;
    };

    Promise.all([
      fetchStocks("XU030"),
      fetchStocks("XU050"),
      fetchStocks("XU100"),
    ])
      .then(([xu030Stocks, xu050Stocks, xu100Stocks]) => {
        const combinedValue = [
          formatStocks("XU030", xu030Stocks),
          formatFilteredStocks("XU050 - XU030", xu050Stocks, xu030Stocks),
          formatFilteredStocks("XU100 - XU050", xu100Stocks, xu050Stocks),
        ].join(",\n");

        setEditorValue(combinedValue);
      })
      .catch((error) => {
        console.error("Error generating special format:", error);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
          display: "flex",
          justifyContent: "center",
          px: 2,
          py: { xs: 3, sm: 5 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 720 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 4,
            }}
          >
            <Box>
              <Typography variant="h5" component="h1">
                TradingView Liste Oluşturucu
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Borsa İstanbul endeksleri için izleme listesi
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                color: "primary.main",
                fontFamily: "'SFMono-Regular', Menlo, Consolas, monospace",
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
              aria-label="Kapsanan piyasalar"
            >
              {markets.length > 0
                ? markets.map((m) => m.code).join(" · ")
                : "BIST · KAP"}
            </Box>
            <Button
              onClick={() => setIsDark((v) => !v)}
              aria-label={isDark ? "Açık temaya geç" : "Koyu temaya geç"}
              aria-pressed={isDark}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                minWidth: 40,
                minHeight: 32,
                px: 1,
                fontWeight: 700,
                lineHeight: 1,
              }}
              title={isDark ? "Açık temaya geç" : "Koyu temaya geç"}
            >
              {isDark ? "☀" : "☾"}
            </Button>
          </Box>

          {/* Market selector */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }} component="label" htmlFor="market-select">
            Piyasa Seçin
          </Typography>
          <Select
            id="market-select"
            aria-label="Piyasa Seçin"
            value={selectedMarket?.market_id ?? ""}
            onChange={(e) => {
              const market = markets.find((m) => m.market_id === e.target.value);
              setSelectedMarket(market || null);
              setIsSpecialOption(false);
              // Reset the sub-selection to the first index of the new market
              const first = market
                ? exchanges.find((ex) => ex.market_id === market.market_id)
                : null;
              setSelectedExchange(first || null);
            }}
            displayEmpty
            fullWidth
            sx={{ mb: 2 }}
          >
            {markets.length === 0 && !loadError && (
              <MenuItem value="" disabled>
                Piyasalar yükleniyor…
              </MenuItem>
            )}
            {markets.map((market) => (
              <MenuItem key={market.market_id} value={market.market_id}>
                {market.code + (market.type ? " — " + market.type : "")}
              </MenuItem>
            ))}
          </Select>

          {/* Exchange (index) selector — scoped to the selected market */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }} component="label" htmlFor="exchange-select">
            Endeks Seçin
          </Typography>
          <Select
            id="exchange-select"
            aria-label="Endeks Seçin"
            value={isSpecialOption ? "special" : selectedExchange?.code || ""}
            onChange={async (e) => {
              const selectedValue = e.target.value;
              if (selectedValue === "special") {
                setIsSpecialOption(true);
                generateSpecialFormat();
              } else {
                setIsSpecialOption(false);
                const exchange = exchanges.find((exchange) => exchange.code === selectedValue);
                setSelectedExchange(exchange);
                if (exchange) {
                  await fetchAndSetStocks(exchange, setEditorValue, tvPrefixes);
                }
              }
            }}
            displayEmpty
            fullWidth
            disabled={!selectedMarket}
          >
            {!selectedMarket && (
              <MenuItem value="" disabled>
                Önce piyasa seçin
              </MenuItem>
            )}
            {selectedMarket &&
              exchanges
                .filter((exchange) => exchange.market_id === selectedMarket.market_id)
                .map((exchange) => (
                  <MenuItem key={exchange.code} value={exchange.code}>
                    {exchange.code + " — " + exchange.name}
                  </MenuItem>
                ))}
            {selectedMarket?.code === "BIST" && (
              <MenuItem value="special">
                XU030 &amp; XU050−XU030 &amp; XU100−XU050
              </MenuItem>
            )}
          </Select>

          {/* Error state */}
          {loadError && (
            <Box
              role="alert"
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "error.main",
                bgcolor: "rgba(234, 57, 67, 0.08)",
                color: "error.main",
                fontSize: "0.875rem",
              }}
            >
              Endeks listesi alınamadı. Bağlantınızı kontrol edip sayfayı
              yenileyin.
            </Box>
          )}

          {/* Output */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 4, mb: 1 }} component="label" htmlFor="watchlist-output">
            İzleme Listesi
          </Typography>
          <textarea
            id="watchlist-output"
            className="output-panel"
            value={editorValue}
            onChange={(e) => setEditorValue(e.target.value)}
            placeholder="###XU100, BIST:AEFES, BIST:AGHOL, …"
            spellCheck={false}
            aria-label="TradingView izleme listesi çıktısı"
          />

          {/* Copy CTA */}
          <CopyButton
            variant={copy_success ? "outlined" : "contained"}
            color="primary"
            fullWidth
            onClick={handleCopy}
            copied={copy_success}
            disabled={!editorValue}
            sx={{ mt: 2 }}
          >
            {copy_success ? "✓ Kopyalandı!" : "Kopyala"}
          </CopyButton>

          {/* Footer */}
          <Typography
            variant="caption"
            color="text.secondary"
            component="footer"
            sx={{ display: "block", textAlign: "center", mt: 5 }}
          >
            Doruk Çankaya
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TwPanel;
